import { useEffect } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { analytics } from './lib/analytics'
import { AppLayout } from './components/layout/AppLayout.tsx'
import { HealthPage } from './pages/HealthPage.tsx'
import { LockerRoomPage } from './pages/locker-room/LockerRoomPage.tsx'
import { GearPage } from './pages/gear/GearPage.tsx'
import { KitCollectionsPage } from './pages/kit-collections/KitCollectionsPage.tsx'
import { LockersPage } from './pages/lockers/LockersPage.tsx'
import { BinsPage } from './pages/lockers/BinsPage.tsx'
import { CommunityPage } from './pages/community/CommunityPage.tsx'
import { ProfilePage } from './pages/profile/ProfilePage.tsx'
import { SettingsPage } from './pages/settings/SettingsPage.tsx'
import { PlaceholderPage } from './components/ui/PlaceholderPage.tsx'
import { RecentlyDeletedPage } from './pages/gear/RecentlyDeletedPage.tsx'
import { LandingPage } from './pages/landing/LandingPage.tsx'
import { AuthCallbackPage } from './pages/auth/AuthCallbackPage.tsx'
import { ProtectedRoute } from './components/auth/ProtectedRoute.tsx'

function usePageviewTracking() {
  const location = useLocation()
  useEffect(() => {
    analytics.pageview(location.pathname)
  }, [location.pathname])
}

export default function App() {
  usePageviewTracking()

  return (
    <Routes>
      {/* Public — no auth required */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LandingPage />} />
      <Route path="/health" element={<HealthPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/share/:token" element={<PlaceholderPage title="Shared Gear Bag" description="Someone has shared their gear bag with you" />} />

      {/* Protected — auth required */}
      <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        {/* Locker Room */}
        <Route path="/locker-room" element={<LockerRoomPage />} />

        {/* Lockers */}
        <Route path="/lockers" element={<LockersPage />} />
        <Route path="/lockers/default" element={<LockersPage />} />
        <Route path="/lockers/default/bins" element={<BinsPage />} />
        <Route path="/lockers/:id" element={<LockersPage />} />
        <Route path="/lockers/:id/bins" element={<BinsPage />} />

        {/* Gear */}
        <Route path="/gear" element={<GearPage />} />
        <Route path="/gear/favorites" element={<GearPage favoritesOnly />} />
        <Route path="/gear/favourites" element={<Navigate to="/gear/favorites" replace />} />

        <Route path="/gear/new" element={<GearPage />} />
        <Route path="/gear/:id" element={<GearPage />} />

        {/* Recently Deleted — shared across all object types */}
        <Route path="/recently-deleted" element={<RecentlyDeletedPage />} />

        {/* Kit Collections */}
        <Route path="/kit-collections" element={<KitCollectionsPage />} />
        <Route path="/kit-collections/favorites" element={<KitCollectionsPage favoritesOnly />} />
        <Route path="/kit-collections/shared-with-me" element={<PlaceholderPage title="Shared With Me" description="Gear bags others have shared with you" />} />
        <Route path="/kit-collections/shared-by-me" element={<KitCollectionsPage filterVisibility="shared" />} />
        <Route path="/kit-collections/community" element={<KitCollectionsPage filterVisibility="community" />} />
        <Route path="/kit-collections/public" element={<KitCollectionsPage filterVisibility="public" />} />
        <Route path="/kit-collections/new" element={<KitCollectionsPage />} />
        <Route path="/kit-collections/:id" element={<KitCollectionsPage />} />

        {/* Community */}
        <Route path="/community" element={<CommunityPage />} />
        <Route path="/community/bags" element={<PlaceholderPage title="Browse Bags" description="Discover gear bags shared by the community" />} />

        {/* Profile & Settings */}
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/settings" element={<SettingsPage />} />
      </Route>

      {/* Unknown routes: signed-in users land in the app, everyone else is
          bounced to /login by ProtectedRoute, which renders the landing page. */}
      <Route path="*" element={<Navigate to="/locker-room" replace />} />
    </Routes>
  )
}
